/**
 * @group Constants
 * 
 * @summary
 * <blockquote>Environment variable names for consistent environment variable references across the application</blockquote>
 * 
 * @example
 * ```typescript
 * import { ENVIRONMENT_VALUES } from '@shared/utils';
 * 
 * const dbUrl = process.env[ENVIRONMENT_VALUES.DATABASE.POSTGRES_URL];
 * ```
 * 
 * Environment variables are organized by category:
 * - DATABASE: Database connection variables
 * - AUTH: Authentication and security variables
 * - ANALYTICS: Analytics and tracking variables
 * - LOGGING: Logging and environment detection variables
 * - EMAIL: Email service variables
 * - TESTING: Testing-specific variables
 * - BUILD: Build-time detection variables
 * - SDK: Third-party SDK configuration variables
 * - DOXY: Doxy.me video conferencing service variables
 * - GITHUB: GitHub Actions and repository variables
 * - EXTERNAL_SERVICES: External service API keys and configuration
 * - SHARED_CORE: Shared core dependency injection configuration variables
 * - JAAS: Jitsi as a Service configuration variables
 * - DISTRIBUTOR: Distributor API configuration variables
 * - TELEHEALTH: Telehealth service configuration variables
 */
export const ENVIRONMENT_VALUES = {
  DATABASE: {
    POSTGRES_URL: 'POSTGRES_URL',
    LOCAL_POSTGRES_URL: 'LOCAL_POSTGRES_URL',
    OVERRIDE_POSTGRES_URL: 'OVERRIDE_POSTGRES_URL'
  },
  AUTH: {
    NEXT_PUBLIC_AUTH_SECRET: 'NEXT_PUBLIC_AUTH_SECRET',
    AUTH_JWT_EXPIRY: 'AUTH_JWT_EXPIRY',
    AUTH_SESSION_MAX_AGE: 'AUTH_SESSION_MAX_AGE',
  },
  ANALYTICS: {
    NEXT_PUBLIC_POSTHOG_KEY: 'NEXT_PUBLIC_POSTHOG_KEY',
    NEXT_PUBLIC_POSTHOG_HOST: 'NEXT_PUBLIC_POSTHOG_HOST',
    EXPO_PUBLIC_POSTHOG_KEY: 'EXPO_PUBLIC_POSTHOG_KEY',
    EXPO_PUBLIC_POSTHOG_HOST: 'EXPO_PUBLIC_POSTHOG_HOST'
  },
  LOGGING: {
    NEXT_PUBLIC_NODE_ENV: 'NEXT_PUBLIC_NODE_ENV',
    NEXT_PUBLIC_VERCEL_ENV: 'NEXT_PUBLIC_VERCEL_ENV',
    LOG_LEVEL: 'LOG_LEVEL',
    DEBUG: 'DEBUG',
    DEBUG_AUTH: 'DEBUG_AUTH',
    AXIOM_DATASET: 'AXIOM_DATASET',
    AXIOM_TOKEN: 'AXIOM_TOKEN',
    SENTRY_DSN: 'SENTRY_DSN',
    NEXT_PUBLIC_SENTRY_DSN: 'NEXT_PUBLIC_SENTRY_DSN',
    EXPO_PUBLIC_SENTRY_DSN: 'EXPO_PUBLIC_SENTRY_DSN'
  },
  EMAIL: {
    RESEND_API_KEY: 'RESEND_API_KEY',
    EMAIL_FROM: 'EMAIL_FROM',
    EMAIL_REPLY_TO: 'EMAIL_REPLY_TO'
  },
  TESTING: {
    TEST_ENV_FILE: '.env.test.local',
    JEST_WORKER_ID: 'JEST_WORKER_ID'
  },
  URL: {
    STAGING_API_URL: 'STAGING_API_URL',
    PRODUCTION_API_URL: 'PRODUCTION_API_URL',
    RESEND_URL: 'RESEND_URL',
    AXIOM_URL: 'AXIOM_URL',
    VERCEL_PROJECT_PRODUCTION_URL: 'VERCEL_PROJECT_PRODUCTION_URL',
    NEXT_PUBLIC_APP_URL: 'NEXT_PUBLIC_APP_URL',
    NEXT_PUBLIC_URL: 'NEXT_PUBLIC_URL',
    // Mobile API URLs
    EXPO_PUBLIC_API_URL: 'EXPO_PUBLIC_API_URL',
    EXPO_PUBLIC_PREVIEW_URL: 'EXPO_PUBLIC_PREVIEW_URL',
    VERCEL_URL: 'VERCEL_URL',
    // Mobile API fallback URLs
    MOBILE_PREVIEW_FALLBACK_URL: 'MOBILE_PREVIEW_FALLBACK_URL',
    MOBILE_PRODUCTION_FALLBACK_URL: 'MOBILE_PRODUCTION_FALLBACK_URL',
    // Default URLs (can be overridden by environment variables)
    DEFAULT_DEVELOPMENT_URL: 'DEFAULT_DEVELOPMENT_URL',
    DEFAULT_PREVIEW_URL: 'DEFAULT_PREVIEW_URL',
    DEFAULT_PRODUCTION_URL: 'DEFAULT_PRODUCTION_URL',
    EXPO_PUBLIC_ANDROID_DOWNLOAD_URL: 'EXPO_PUBLIC_ANDROID_DOWNLOAD_URL', // Public Android app download page URL
    EXPO_PUBLIC_VERSION_GATE_POLICY_URL: 'EXPO_PUBLIC_VERSION_GATE_POLICY_URL', // Public URL for Android version gate policy JSON
  },
  BUILD: {
    NEXT_PHASE: 'NEXT_PHASE',
    RAILWAY_ENVIRONMENT: 'RAILWAY_ENVIRONMENT'
  },
  VERCEL: {
    VERCEL_ENV: 'VERCEL_ENV',
    VERCEL_GIT_COMMIT_REF: 'VERCEL_GIT_COMMIT_REF',
    VERCEL_GIT_COMMIT_SHA: 'VERCEL_GIT_COMMIT_SHA',
    VERCEL_GIT_PULL_REQUEST_ID: 'VERCEL_GIT_PULL_REQUEST_ID',
  },
  SHENAI: {
    SHENAI_API_KEY: 'SHENAI_API_KEY',
    ADMIN_CLIENT_ID: 'SHENAI_ADMIN_CLIENT_ID', // ShenAI Admin Portal client ID for token generation
    ADMIN_CLIENT_SECRET: 'SHENAI_ADMIN_CLIENT_SECRET', // ShenAI Admin Portal client secret for token generation
  },
  OPENEMR: {
    BASE_URL: 'OPENEMR_BASE_URL',
    CLIENT_ID: 'OPENEMR_CLIENT_ID',
    CLIENT_SECRET: 'OPENEMR_CLIENT_SECRET',
    USERNAME: 'OPENEMR_USERNAME',
    PASSWORD: 'OPENEMR_PASSWORD',
    REFRESH_TOKEN: 'OPENEMR_REFRESH_TOKEN'
  },
  PAYSTACK: {
    SECRET_KEY: 'PAYSTACK_SECRET_KEY',
    PUBLIC_KEY: 'PAYSTACK_PUBLIC_KEY'
  },
  SHARED_CORE: {
    SHARED_CORE_DI_STRICT_MODE: 'SHARED_CORE_DI_STRICT_MODE',
    SHARED_CORE_DI_INJECTION_MODE: 'SHARED_CORE_DI_INJECTION_MODE',
    SHARED_CORE_DI_BOUNDARY_ENFORCEMENT: 'SHARED_CORE_DI_BOUNDARY_ENFORCEMENT',
  },
  DOXY: {
    NEXT_PUBLIC_DOXY_URL: 'NEXT_PUBLIC_DOXY_URL',
    EXPO_PUBLIC_DOXY_URL: 'EXPO_PUBLIC_DOXY_URL'
  },
  GITHUB: {
    GITHUB_REPOSITORY_OWNER: 'GITHUB_REPOSITORY_OWNER',
    GITHUB_REPOSITORY_NAME: 'GITHUB_REPOSITORY_NAME',
    GITHUB_TOKEN: 'GITHUB_TOKEN',
    GITHUB_ACTIONS: 'GITHUB_ACTIONS'
  },
  EXTERNAL_SERVICES: {
    OPENAI_API_KEY: 'OPENAI_API_KEY',
    GEMINI_API_KEY: 'GEMINI_API_KEY',
    AWS_VERSION_GATE_S3_BUCKET: 'AWS_VERSION_GATE_S3_BUCKET', // S3 bucket for Android version gate policy
    AWS_VERSION_GATE_S3_KEY: 'AWS_VERSION_GATE_S3_KEY', // S3 object key for Android version gate policy JSON
  },
  FEATURE_FLAGS: {
    VERSION_GATE_FAILURE_MODE: 'VERSION_GATE_FAILURE_MODE',
    VERSION_GATE_SIGNIN_ENFORCEMENT: 'VERSION_GATE_SIGNIN_ENFORCEMENT',
  },
  SCANNER: {
    SCANNER_API_KEY: 'SCANNER_API_KEY',
    SCANNER_API_URL: 'SCANNER_API_URL',
  },
  JAAS: {
    JAAS_APP_ID: 'JAAS_APP_ID', // JaaS tenant identifier (vpaas-magic-cookie-...)
    JAAS_API_KEY_ID: 'JAAS_API_KEY_ID', // JaaS API key ID for kid header
    JAAS_PRIVATE_KEY: 'JAAS_PRIVATE_KEY', // RSA private key (PKCS8 PEM) for RS256 signing
    JAAS_DOMAIN: 'JAAS_DOMAIN', // JaaS server domain (default 8x8.vc)
  },

  DISTRIBUTOR: {
    DISTRIBUTOR_API_KEY: 'DISTRIBUTOR_API_KEY', // Distributor API key for patient creation
  },



  TELEHEALTH: {
    TELEHEALTH_JOIN_TICKET_TTL_SECONDS: 'TELEHEALTH_JOIN_TICKET_TTL_SECONDS', // Stateless HMAC JoinTicket lifetime (default 300)
    TELEHEALTH_SESSION_CREDENTIAL_TTL_SECONDS: 'TELEHEALTH_SESSION_CREDENTIAL_TTL_SECONDS', // Provider session credential lifetime (default 600)
    TELEHEALTH_JOIN_TICKET_SIGNING_SECRET: 'TELEHEALTH_JOIN_TICKET_SIGNING_SECRET', // HMAC secret for JoinTicket signing
    TELEHEALTH_DEFAULT_CLINICIAN_USER_ID: 'TELEHEALTH_DEFAULT_CLINICIAN_USER_ID', // Default clinician for consultation assignment (v1)
    TELEHEALTH_STALE_THRESHOLD_SECONDS: 'TELEHEALTH_STALE_THRESHOLD_SECONDS', // Seconds of inactivity before a waiting queue entry is cancelled (default: 120)
  },
} as const;